import { Component, inject, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { USPSAddress, USPSLabelRequest, USPSLabelResponse, USPSPriceResponse, HostPortalClient, MyAccountClient, StateList } from 'src/app/Services/WOWApi.Service';
import { CommonServices } from 'src/app/Common/CommonServices';
import { forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-from-address-dialog',
  standalone: false,
  templateUrl: './from-address-dialog.component.html',
  styleUrls: ['./from-address-dialog.component.css']
})
export class FromAddressDialogComponent implements OnInit {
  @ViewChild('labelErrorPanel') labelErrorPanel?: ElementRef<HTMLElement>;

  private readonly dialogRef = inject(MatDialogRef<FromAddressDialogComponent>);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly hostPortalClient = inject(HostPortalClient);
  private readonly myAccountClient = inject(MyAccountClient);
  private readonly commonServices = inject(CommonServices);

  fromAddress: USPSAddress = USPSAddress.fromJS({
    name: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });
  fromFirstName = '';
  fromLastName = '';

  toAddress: USPSAddress = USPSAddress.fromJS({
    name: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });
  toFirstName = '';
  toLastName = '';
  defaultToAddress: USPSAddress | null = null;
  useDifferentToAddress = false;

  statelist: StateList[] = [];
  selectedFromState: StateList | null = null;
  selectedToState: StateList | null = null;

  // Package details
  packageWeight: number;
  packageLength: number;
  packageWidth: number;
  packageHeight: number;
  selectedServiceTypes: { [key: string]: boolean } = {
    'Priority': false,
    'FirstClass': false,
    'PriorityExpress': false,
    'GroundAdvantage': false,
    'ParcelSelect': false,
    'ParcelSelectLightweight': false
  };
  selectedServiceTypeForLabel: string; // Selected service type for creating label
  labelFormat: string = 'PDF';
  saveFromAddress = false;

  isCreatingLabel: boolean = false;
  isCheckingPrice: boolean = false;
  
  // Price information for multiple service types
  priceResults: Array<{
    serviceType: string;
    serviceTypeDisplay: string;
    price: number;
    description: string;
    priceType: string;
    error?: string;
  }> = [];
  priceError: string;
  labelError: string | null = null;

  ngOnInit(): void {
    this.getStateList();

    // If data is provided, populate the form with existing values
    if (this.data) {
      // From address from previous label on this shipment
      if (this.data.fromAddress) {
        this.populateFromAddress(this.data.fromAddress);
      } else if (this.data.webinarRoomId) {
        this.loadSavedFromAddress(this.data.webinarRoomId);
      }

      // Package details from shipment
      if (this.data.shipment) {
        this.packageWeight = this.data.shipment.packageWeight || undefined;
        this.packageLength = this.data.shipment.packageLength || undefined;
        this.packageWidth = this.data.shipment.packageWidth || undefined;
        this.packageHeight = this.data.shipment.packageHeight || undefined;
        const existingServiceType = this.data.shipment.uspsServiceType;
        if (existingServiceType && this.selectedServiceTypes.hasOwnProperty(existingServiceType)) {
          this.selectedServiceTypes[existingServiceType] = true;
          this.selectedServiceTypeForLabel = existingServiceType;
        }
        this.labelFormat = this.data.shipment.labelFormat || 'PDF';
        this.initializeToAddressFromShipment();
      }
    }
  }

  populateFromAddress(address: USPSAddress): void {
    this.fromAddress = USPSAddress.fromJS({
      name: address.name || '',
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zipCode || '',
      phone: address.phone || ''
    });
    const split = this.splitName(this.fromAddress.name);
    this.fromFirstName = split.firstName;
    this.fromLastName = split.lastName;
    this.setSelectedFromState();
  }

  loadSavedFromAddress(webinarRoomId: number): void {
    this.hostPortalClient.getSavedFromAddress(webinarRoomId).subscribe(
      (savedAddress) => {
        if (!savedAddress) {
          return;
        }

        this.populateFromAddress(USPSAddress.fromJS({
          name: savedAddress.name,
          street: savedAddress.street,
          city: savedAddress.city,
          state: savedAddress.state,
          zipCode: savedAddress.zipCode,
          phone: savedAddress.phone
        }));
      },
      () => {
        // No saved address is fine; host can enter a new one.
      }
    );
  }

  initializeToAddressFromShipment(): void {
    const shipment = this.data?.shipment;
    if (!shipment?.shipmentAddress) {
      return;
    }

    this.defaultToAddress = USPSAddress.fromJS({
      name: shipment.shipmentAddress.name || shipment.newOwner || '',
      street: shipment.shipmentAddress.street || '',
      city: shipment.shipmentAddress.city || '',
      state: this.getStateCode(shipment.shipmentAddress.state),
      zipCode: shipment.shipmentAddress.zipCode ? String(shipment.shipmentAddress.zipCode) : '',
      phone: shipment.shipmentAddress.phone || ''
    });
    this.toAddress = USPSAddress.fromJS(this.defaultToAddress.toJSON());
    const split = this.splitName(this.toAddress.name);
    this.toFirstName = split.firstName;
    this.toLastName = split.lastName;
    this.setSelectedToState();
  }

  getStateList(): void {
    this.myAccountClient.getStateList().subscribe(
      response => {
        this.statelist = response || [];
        this.setSelectedFromState();
        this.setSelectedToState();
      },
      () => {
        this.commonServices.showMessage('error', 'Error loading state list', 5);
      }
    );
  }

  setSelectedFromState(): void {
    if (!this.fromAddress?.state || !this.statelist?.length) {
      return;
    }

    const stateValue = this.fromAddress.state.trim();
    this.selectedFromState = this.statelist.find(
      s => s.stateCode?.toUpperCase() === stateValue.toUpperCase() ||
           s.name?.toUpperCase() === stateValue.toUpperCase()
    ) || null;
  }

  onFromStateChange(state: StateList): void {
    this.selectedFromState = state;
    this.fromAddress.state = this.getStateCode(state);
  }

  onToStateChange(state: StateList): void {
    this.selectedToState = state;
    this.toAddress.state = this.getStateCode(state);
  }

  onUseDifferentToAddressChange(useDifferent: boolean): void {
    this.useDifferentToAddress = useDifferent;
    if (!useDifferent && this.defaultToAddress) {
      this.toAddress = USPSAddress.fromJS(this.defaultToAddress.toJSON());
      const split = this.splitName(this.toAddress.name);
      this.toFirstName = split.firstName;
      this.toLastName = split.lastName;
      this.setSelectedToState();
    }
  }

  setSelectedToState(): void {
    if (!this.toAddress?.state || !this.statelist?.length) {
      return;
    }

    const stateValue = this.toAddress.state.trim();
    this.selectedToState = this.statelist.find(
      s => s.stateCode?.toUpperCase() === stateValue.toUpperCase() ||
           s.name?.toUpperCase() === stateValue.toUpperCase()
    ) || null;
  }

  getStateCode(state: StateList | { stateCode?: string; name?: string } | null | undefined): string {
    if (!state) {
      return '';
    }
    return (state.stateCode || '').trim().toUpperCase();
  }

  normalizePhone(phone: string | undefined): string {
    return (phone || '').replace(/\D/g, '');
  }

  splitName(fullName: string | undefined): { firstName: string; lastName: string } {
    const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ')
    };
  }

  combineName(firstName: string | undefined, lastName: string | undefined): string {
    return `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim();
  }

  isValidPhone(phone: string | undefined): boolean {
    const digits = this.normalizePhone(phone);
    return /^[1-9][0-9]{5,14}$/.test(digits);
  }

  isValidZipCode(zipCode: string | undefined): boolean {
    const zip = (zipCode || '').trim();
    return /^\d{5}(\d{4})?$/.test(zip);
  }

  hasFullName(name: string | undefined): boolean {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return parts.length >= 2;
  }

  isFromAddressValid(): boolean {
    return !!this.fromFirstName?.trim() &&
      !!this.fromLastName?.trim() &&
      !!this.fromAddress.street?.trim() &&
      !!this.fromAddress.city?.trim() &&
      !!this.selectedFromState &&
      !!this.getStateCode(this.selectedFromState) &&
      this.isValidZipCode(this.fromAddress.zipCode) &&
      this.isValidPhone(this.fromAddress.phone);
  }

  isPackageDetailsValid(): boolean {
    return this.packageWeight > 0 &&
      this.packageLength > 0 &&
      this.packageWidth > 0 &&
      this.packageHeight > 0;
  }

  validateFromAddress(showMessage: boolean = true): boolean {
    if (!this.fromFirstName?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter your first name', 5);
      return false;
    }
    if (!this.fromLastName?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter your last name', 5);
      return false;
    }
    if (!this.fromAddress.street?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter street address', 5);
      return false;
    }
    if (!this.fromAddress.city?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter city', 5);
      return false;
    }
    if (!this.selectedFromState || !this.getStateCode(this.selectedFromState)) {
      if (showMessage) this.commonServices.showMessage('error', 'Please select state', 5);
      return false;
    }
    if (!this.isValidZipCode(this.fromAddress.zipCode)) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter a valid ZIP code (5 or 9 digits)', 5);
      return false;
    }
    if (!this.isValidPhone(this.fromAddress.phone)) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter a valid phone number (10-15 digits, no leading zero)', 5);
      return false;
    }
    return true;
  }

  isToAddressValid(): boolean {
    if (!this.useDifferentToAddress) {
      return !!this.defaultToAddress &&
        this.hasFullName(this.defaultToAddress.name) &&
        !!this.defaultToAddress.street?.trim() &&
        !!this.defaultToAddress.city?.trim() &&
        !!this.defaultToAddress.state &&
        this.isValidZipCode(this.defaultToAddress.zipCode);
    }

    return !!this.toFirstName?.trim() &&
      !!this.toLastName?.trim() &&
      !!this.toAddress.street?.trim() &&
      !!this.toAddress.city?.trim() &&
      !!this.selectedToState &&
      !!this.getStateCode(this.selectedToState) &&
      this.isValidZipCode(this.toAddress.zipCode);
  }

  validateToAddress(showMessage: boolean = true): boolean {
    if (!this.useDifferentToAddress) {
      if (!this.defaultToAddress) {
        if (showMessage) this.commonServices.showMessage('error', 'Winner shipping address not found', 5);
        return false;
      }
      if (!this.hasFullName(this.defaultToAddress.name)) {
        if (showMessage) this.commonServices.showMessage('error', 'Recipient name must include first and last name', 5);
        return false;
      }
      if (!this.defaultToAddress.street?.trim() || !this.defaultToAddress.city?.trim() || !this.defaultToAddress.state) {
        if (showMessage) this.commonServices.showMessage('error', 'Winner shipping address is incomplete', 5);
        return false;
      }
      if (!this.isValidZipCode(this.defaultToAddress.zipCode)) {
        if (showMessage) this.commonServices.showMessage('error', 'Winner ZIP code is missing or invalid', 5);
        return false;
      }
      return true;
    }

    if (!this.toFirstName?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter recipient first name', 5);
      return false;
    }
    if (!this.toLastName?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter recipient last name', 5);
      return false;
    }
    if (!this.toAddress.street?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter recipient street address', 5);
      return false;
    }
    if (!this.toAddress.city?.trim()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter recipient city', 5);
      return false;
    }
    if (!this.selectedToState || !this.getStateCode(this.selectedToState)) {
      if (showMessage) this.commonServices.showMessage('error', 'Please select recipient state', 5);
      return false;
    }
    if (!this.isValidZipCode(this.toAddress.zipCode)) {
      if (showMessage) this.commonServices.showMessage('error', 'Please enter a valid recipient ZIP code (5 or 9 digits)', 5);
      return false;
    }
    return true;
  }

  getEffectiveToAddress(): USPSAddress | null {
    if (!this.useDifferentToAddress) {
      if (!this.defaultToAddress) {
        return null;
      }

      return USPSAddress.fromJS(this.defaultToAddress.toJSON());
    }

    return USPSAddress.fromJS({
      name: this.combineName(this.toFirstName, this.toLastName),
      street: this.toAddress.street || '',
      city: this.toAddress.city || '',
      state: this.getStateCode(this.selectedToState),
      zipCode: this.toAddress.zipCode || '',
      phone: this.normalizePhone(this.toAddress.phone)
    });
  }

  getDefaultToAddressDisplay(): string {
    if (!this.defaultToAddress) {
      return 'No shipping address on file for the winner';
    }

    return `${this.defaultToAddress.name}, ${this.defaultToAddress.street}, ${this.defaultToAddress.city}, ${this.defaultToAddress.state} ${this.defaultToAddress.zipCode}${this.defaultToAddress.phone ? ' | Phone: ' + this.defaultToAddress.phone : ''}`;
  }

  validatePackageDetails(showMessage: boolean = true): boolean {
    if (!this.isPackageDetailsValid()) {
      if (showMessage) this.commonServices.showMessage('error', 'Please fill in all package dimensions and weight', 5);
      return false;
    }
    return true;
  }

  onSave(): void {
    if (!this.validateFromAddress() || !this.validateToAddress() || !this.validatePackageDetails()) {
      return;
    }

    if (!this.selectedServiceTypeForLabel) {
      this.commonServices.showMessage('error', 'Please select a service type for the label', 5);
      return;
    }

    const toAddress = this.getEffectiveToAddress();
    if (!toAddress) {
      this.commonServices.showMessage('error', 'Destination address not found', 5);
      return;
    }

    if (!this.data?.shipment?.id) {
      this.commonServices.showMessage('error', 'Shipment information not available', 5);
      return;
    }

    this.fromAddress.name = this.combineName(this.fromFirstName, this.fromLastName);
    this.fromAddress.state = this.getStateCode(this.selectedFromState);
    this.fromAddress.phone = this.normalizePhone(this.fromAddress.phone);
    this.labelError = null;
    this.isCreatingLabel = true;

    const request = USPSLabelRequest.fromJS({
      fromAddress: this.fromAddress,
      toAddress: toAddress,
      weight: this.packageWeight,
      length: this.packageLength,
      width: this.packageWidth,
      height: this.packageHeight,
      serviceType: this.selectedServiceTypeForLabel,
      labelFormat: this.labelFormat || 'PDF',
      saveFromAddress: this.saveFromAddress,
      webinarRoomId: this.data?.webinarRoomId
    });

    this.hostPortalClient.createUSPSLabel(this.data.shipment.id, request).subscribe(
      (response: USPSLabelResponse) => {
        this.isCreatingLabel = false;

        if (this.isSuccessfulLabelResponse(response)) {
          this.dialogRef.close({
            success: true,
            shipmentId: this.data.shipment.id,
            response: response,
            labelDetails: {
              serviceType: this.selectedServiceTypeForLabel,
              labelFormat: this.labelFormat || 'PDF',
              packageWeight: this.packageWeight,
              packageLength: this.packageLength,
              packageWidth: this.packageWidth,
              packageHeight: this.packageHeight,
              fromAddress: this.fromAddress
            }
          });
          return;
        }

        this.showLabelError(this.formatLabelError(this.getErrorMessage(response)));
      },
      (error) => {
        this.isCreatingLabel = false;
        this.showLabelError(this.formatLabelError(this.extractErrorMessage(error)));
      }
    );
  }

  private showLabelError(message: string): void {
    this.labelError = message;
    setTimeout(() => this.scrollToLabelError());
  }

  private scrollToLabelError(): void {
    const element = this.labelErrorPanel?.nativeElement
      ?? document.getElementById('label-error-panel');
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }

  private isSuccessfulLabelResponse(response: USPSLabelResponse | any): boolean {
    return response?.success === true || response?.Success === true;
  }

  private getErrorMessage(response: USPSLabelResponse | any): string | undefined {
    return response?.errorMessage ?? response?.ErrorMessage;
  }

  extractErrorMessage(error: any): string {
    if (error?.error?.errorMessage) {
      return error.error.errorMessage;
    }
    if (error?.error?.ErrorMessage) {
      return error.error.ErrorMessage;
    }
    if (typeof error?.error === 'string') {
      return error.error;
    }
    if (error?.error?.message) {
      return error.error.message;
    }
    if (error?.message) {
      return error.message;
    }
    return 'Unknown error creating USPS label';
  }

  formatLabelError(errorMessage: string | undefined): string {
    const details = this.extractErrorDetails(errorMessage);
    if (details.length) {
      return details.join('\n');
    }

    return 'Failed to create USPS label';
  }

  private extractErrorDetails(errorMessage: string | undefined): string[] {
    if (!errorMessage?.trim()) {
      return [];
    }

    const jsonStart = errorMessage.indexOf('{');
    const jsonEnd = errorMessage.lastIndexOf('}');
    if (jsonStart >= 0 && jsonEnd > jsonStart) {
      try {
        const parsed = JSON.parse(errorMessage.substring(jsonStart, jsonEnd + 1));
        const details = this.collectErrorDetails(parsed);
        if (details.length) {
          return details;
        }
      } catch {
        // Continue to fallback parsing.
      }
    }

    try {
      return this.collectErrorDetails(JSON.parse(errorMessage));
    } catch {
      return [];
    }
  }

  private collectErrorDetails(parsed: any): string[] {
    const errors = parsed?.error?.errors ?? parsed?.errors;
    if (!Array.isArray(errors)) {
      return [];
    }

    return errors
      .map((item: { detail?: string }) => item.detail?.trim())
      .filter((detail: string | undefined): detail is string => !!detail);
  }

  getSelectedServiceTypes(): string[] {
    return Object.keys(this.selectedServiceTypes).filter(key => this.selectedServiceTypes[key]);
  }

  getServiceTypeDisplayName(serviceType: string): string {
    const displayNames: { [key: string]: string } = {
      'Priority': 'Priority Mail',
      'FirstClass': 'First-Class Package',
      'PriorityExpress': 'Priority Mail Express',
      'GroundAdvantage': 'USPS Ground Advantage',
      'ParcelSelect': 'USPS Parcel Select',
      'ParcelSelectLightweight': 'USPS Parcel Select Lightweight'
    };
    return displayNames[serviceType] || serviceType;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onCheckPrice(): void {
    if (!this.validateFromAddress() || !this.validateToAddress() || !this.validatePackageDetails()) {
      return;
    }

    const selectedTypes = this.getSelectedServiceTypes();
    if (selectedTypes.length === 0) {
      this.commonServices.showMessage('error', 'Please select at least one USPS Service Type', 5);
      return;
    }

    if (!this.data || !this.data.shipment || !this.data.shipment.id) {
      this.commonServices.showMessage('error', 'Shipment information not available', 5);
      return;
    }

    const toAddress = this.getEffectiveToAddress();
    if (!toAddress) {
      this.commonServices.showMessage('error', 'Destination address not found', 5);
      return;
    }

    this.fromAddress.name = this.combineName(this.fromFirstName, this.fromLastName);
    this.fromAddress.state = this.getStateCode(this.selectedFromState);
    this.fromAddress.phone = this.normalizePhone(this.fromAddress.phone);

    this.isCheckingPrice = true;
    this.priceError = null;
    this.priceResults = [];

    const shipment = this.data.shipment;

    // Make API calls for each selected service type using forkJoin
    const priceRequests = selectedTypes.map(serviceType => {
      const request = USPSLabelRequest.fromJS({
        fromAddress: this.fromAddress,
        toAddress: toAddress,
        weight: this.packageWeight,
        length: this.packageLength,
        width: this.packageWidth,
        height: this.packageHeight,
        serviceType: serviceType,
        labelFormat: this.labelFormat || 'PDF'
      });

      return this.hostPortalClient.getUSPSPrice(shipment.id, request).pipe(
        map((response: USPSPriceResponse) => {
          return {
            serviceType: serviceType,
            serviceTypeDisplay: this.getServiceTypeDisplayName(serviceType),
            price: response.success && response.totalBasePrice !== undefined ? response.totalBasePrice : null,
            description: response.priceDescription || '',
            priceType: response.priceType || '',
            error: response.success ? null : (response.errorMessage || "Unable to retrieve price")
          };
        }),
        catchError((error) => {
          return of({
            serviceType: serviceType,
            serviceTypeDisplay: this.getServiceTypeDisplayName(serviceType),
            price: null,
            description: '',
            priceType: '',
            error: error.error?.errorMessage || error.message || "Error checking price"
          });
        })
      );
    });

    // Wait for all requests to complete
    forkJoin(priceRequests).subscribe(
      (results) => {
        this.isCheckingPrice = false;
        this.priceResults = results;
        
        const successCount = results.filter(r => r.price !== null).length;
        if (successCount > 0) {
          this.commonServices.showMessage("Success", `Retrieved prices for ${successCount} service type(s)`, 5);
          // Auto-select the first successful service type if none selected
          if (!this.selectedServiceTypeForLabel) {
            const firstSuccess = results.find(r => r.price !== null);
            if (firstSuccess) {
              this.selectedServiceTypeForLabel = firstSuccess.serviceType;
            }
          }
        } else {
          this.priceError = "Unable to retrieve prices for any selected service types";
          this.commonServices.showMessage("error", this.priceError, 5);
        }
      },
      (error) => {
        this.isCheckingPrice = false;
        this.priceError = "Error checking prices";
        this.commonServices.showMessage("error", this.priceError, 5);
      }
    );
  }

  onServiceTypeSelected(serviceType: string): void {
    if (serviceType) {
      this.selectedServiceTypeForLabel = serviceType;
    }
  }
}

